import { AxiosResponse } from "axios";
import { ApiBrave } from "../../Apis/Brave";
import {
  AgendamentoByHourProps,
  DownloadProps,
  IAgendamentoAlterarTipoForm,
  IAgendamentoBasicoForm,
  IAgendamentoCadastroForm,
  IAgendamentoDTO,
  IAgendamentosDoDiaDTO,
  IAtendimentoConcessionariaProps,
  IAtendimentoDomiciliarForm,
  ICancelamentoDTO,
  ICancelarProps,
  IConfirmacaoHorarioProps,
  IEventosProps,
  IGetAgendamentosProps,
  IIniciarAgendamentoProps,
  IPageAgendamentoDTO,
  IPageEventoDTO,
  IPutAgendamentoProps,
  IReagendamentoProps,
  IReembolsoConfirmarForm,
  IReembolsoProps,
  IVincularClienteForm,
} from "../../types/agendamento";

import { removeEmpty } from "../../utils/removeEmpty";
import { IVeiculoForm } from "../../types/veiculo";

const basePath = "/agendamento";

function propsToString(params: AgendamentoByHourProps): string {
  const result = params
    ? Object.entries(params)
        .map((e) =>
          Array.isArray(e[1]) ? `${e[0]}=${e[1].join(",")}` : e.join("="),
        )
        .flat()
        .join("&")
    : "";

  return result;
}

export class Agendamento {
  static async get(
    props?: IGetAgendamentosProps,
  ): Promise<AxiosResponse<IPageAgendamentoDTO>> {
    const values = removeEmpty(props);
    const params = new URLSearchParams(values).toString();
    return ApiBrave.get(params ? `${basePath}?${params}` : basePath);
  }

  static async getById({
    uuid,
  }: {
    uuid: string;
  }): Promise<AxiosResponse<IAgendamentoDTO>> {
    return ApiBrave.get(`${basePath}/${uuid}`);
  }

  static async getByHour(
    props: AgendamentoByHourProps,
  ): Promise<AxiosResponse<IAgendamentosDoDiaDTO>> {
    const path = propsToString(props);
    return ApiBrave.get(`${basePath}/listar-por-horario?${path}`);
  }

  static async alterarTipoAtendimento(
    props: IAgendamentoAlterarTipoForm,
  ): Promise<AxiosResponse<IAgendamentoDTO>> {
    const { uuid, ...data } = props;
    return ApiBrave.put(`${basePath}/${uuid}/alterar-tipo-atendimento`, data);
  }

  static async post(
    props: IAgendamentoBasicoForm,
  ): Promise<AxiosResponse<IAgendamentoDTO>> {
    return ApiBrave.post(basePath, props);
  }

  static async postV2(
    props: IAgendamentoCadastroForm,
  ): Promise<AxiosResponse<IAgendamentoDTO>> {
    return ApiBrave.post(`${basePath}/v2`, props);
  }

  static async put(
    props: IPutAgendamentoProps,
  ): Promise<AxiosResponse<IAgendamentoDTO>> {
    const { uuid, ...rest } = props;
    const params = removeEmpty(rest);
    return ApiBrave.put(`${basePath}/${uuid}`, params);
  }

  static async putAddress(props: IAtendimentoDomiciliarForm) {
    const { uuid, ...rest } = props;
    return ApiBrave.put(
      `${basePath}/${uuid}/atualizar-endereco-atendimento`,
      rest,
    );
  }

  static async AtualizarConcessionariaAtedimento(
    props: IAtendimentoConcessionariaProps,
  ): Promise<AxiosResponse<IAgendamentoDTO>> {
    const { uuid, ...rest } = props;

    return ApiBrave.put(
      `${basePath}/${uuid}/atualizar-concessionaria-atendimento`,
      rest,
    );
  }

  static async reagendar(
    props: IReagendamentoProps,
  ): Promise<AxiosResponse<IAgendamentoDTO>> {
    const { uuidAgendamento, ...rest } = props;

    return ApiBrave.put(`${basePath}/${uuidAgendamento}/reagendar`, rest);
  }

  static async iniciar(
    props: IIniciarAgendamentoProps,
  ): Promise<AxiosResponse<IAgendamentoDTO>> {
    const { uuid, ...rest } = props;
    return ApiBrave.put(`${basePath}/${uuid}/iniciar`, rest);
  }

  static async downloadExc(props: DownloadProps) {
    const token = localStorage.getItem("@token");
    let path = `${process.env.REACT_APP_BRAVE_API_URL}${basePath}/listar-deliveries?dia=${props.dia}`;

    if (props?.cidade) {
      path = `${process.env.REACT_APP_BRAVE_API_URL}${basePath}/listar-deliveries?dia=${props.dia}&cidade=${props.cidade}`;
    }

    const response = await fetch(path, {
      method: "GET",
      headers: {
        Authorization: "Bearer " + token.replaceAll('"', ""),
      },
    });
    const blob = await response.blob();

    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "relatorio.xlsx";
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
  }

  static async definirHorario(
    props: IConfirmacaoHorarioProps,
  ): Promise<AxiosResponse<IAgendamentoDTO>> {
    const { uuid, ...rest } = props;
    return ApiBrave.put(`${basePath}/${uuid}/definir-horario`, rest);
  }

  static async cancelar(
    props: ICancelarProps,
  ): Promise<AxiosResponse<ICancelamentoDTO>> {
    const { uuid } = props;

    return ApiBrave.put(`${basePath}/${uuid}/cancelar`);
  }

  static async confirmarPagamento({
    uuid,
  }: {
    uuid: string;
  }): Promise<AxiosResponse<IAgendamentoDTO>> {
    return ApiBrave.put(`${basePath}/${uuid}/confirmacao-manual`);
  }

  static async retornarStatusIniciado({
    uuid,
  }: {
    uuid: string;
  }): Promise<AxiosResponse<IAgendamentoDTO>> {
    return ApiBrave.put(`${basePath}/${uuid}/retornar-status-iniciado`);
  }

  static async vincularAgendamentoAoCliente(
    form: IVincularClienteForm,
  ): Promise<AxiosResponse<IAgendamentoDTO>> {
    const { uuid, ...data } = form;
    const formData = removeEmpty(data);
    return ApiBrave.put(`${basePath}/${uuid}/vincular-cliente`, formData);
  }

  static async vincularAgendamentoAoVeiculo({
    uuidAgendamento,
    uuidVeiculo,
  }: {
    uuidAgendamento: string;
    uuidVeiculo: string;
  }): Promise<AxiosResponse<IAgendamentoDTO>> {
    return ApiBrave.put(`${basePath}/${uuidAgendamento}/vincular-veiculo`, {
      uuid: uuidVeiculo,
    });
  }

  static async colocarEmEspera({
    uuidAgendamento,
  }: {
    uuidAgendamento: string;
  }): Promise<AxiosResponse<IAgendamentoDTO>> {
    return ApiBrave.post(
      `${basePath}/${uuidAgendamento}/alterar-em-espera`,
      true,
    );
  }

  static async atribuir(
    props: IIniciarAgendamentoProps,
  ): Promise<AxiosResponse<IAgendamentoDTO>> {
    const { uuid, ...rest } = props;
    return ApiBrave.put(`${basePath}/${uuid}/atribuir`, rest);
  }

  static async confirmarReembolso(
    props: IReembolsoConfirmarForm,
  ): Promise<AxiosResponse<IAgendamentoDTO>> {
    const { uuid, dataDevolucao } = props;
    return ApiBrave.put(`${basePath}/${uuid}/confirmar-reembolso`, {
      dataDevolucao,
    });
  }

  static async downloadRecibo({
    uuidAgendamento,
  }: {
    uuidAgendamento: string;
  }) {
    const token = localStorage.getItem("@token");
    let path = `${process.env.REACT_APP_BRAVE_API_URL}/agendamento/${uuidAgendamento}/download-recibo`;

    const response = await fetch(path, {
      method: "GET",
      headers: {
        Authorization: "Bearer " + token.replaceAll('"', ""),
      },
    });
    const blob = await response.blob();

    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "fatura.pdf";
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
  }

  static async eventos(
    props: IEventosProps,
  ): Promise<AxiosResponse<IPageEventoDTO>> {
    let params = "";
    const { uuidAgendamento, ...rest } = props;
    if (rest) {
      const values = removeEmpty(rest);
      params = new URLSearchParams(values).toString();
    }

    return ApiBrave.get(
      params
        ? `${basePath}/${uuidAgendamento}/eventos?${params}`
        : `${basePath}/${uuidAgendamento}/eventos`,
    );
  }

  static async reembolsar(
    props: IReembolsoProps,
  ): Promise<AxiosResponse<IAgendamentoDTO>> {
    const { uuid, ...rest } = props;

    const formData = new FormData();

    formData.append(
      "data",
      new Blob([JSON.stringify(rest.data)], { type: "application/json" }),
    );
    formData.append("file", rest.file);

    return ApiBrave.put(`${basePath}/${uuid}/solicitar-reembolso`, formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });
  }

  static async getUrlPesquisa(
    uuidAgendamento: string,
  ): Promise<AxiosResponse<string>> {
    return ApiBrave.get(
      `${basePath}/${uuidAgendamento}/pesquisa-satisfacao-url`,
    );
  }

  static async vincularVeiculoPrimeiroRegistro({
    uuidAgendamento,
    veiculoForm,
  }: {
    uuidAgendamento: string;
    veiculoForm: IVeiculoForm;
  }): Promise<AxiosResponse<IAgendamentoDTO>> {
    return ApiBrave.put(
      `${basePath}/${uuidAgendamento}/vincular-veiculo-primeiro-registro`,
      veiculoForm,
    );
  }

  static async comunicarChegada({uuid} : {uuid: string}) : Promise<void> {
    ApiBrave.post(`${basePath}/${uuid}/comunicar-chegada`);
  }

  static async finalizarManualmente({
    uuid,
    file,
  }: {
    uuid: string;
    file: File;
  }): Promise<AxiosResponse<IAgendamentoDTO>> {
    const formData = new FormData();
    formData.append("file", file);
    return ApiBrave.post(`${basePath}/${uuid}/finalizar-manualmente`, formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
  }

  static async downloadLaudo({ uuid }: { uuid: string }) {
    const token = localStorage.getItem("@token");
    const path = `${process.env.REACT_APP_BRAVE_API_URL}${basePath}/${uuid}/download-laudo`;

    const response = await fetch(path, {
      method: "GET",
      headers: {
        Authorization: "Bearer " + token.replaceAll('"', ""),
      },
    });
    const blob = await response.blob();

    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "laudo.pdf";
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
  }
}